import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useNotifyMobileModal } from "../useNotifyMobileModal";
import { useFormPlayer } from "./useFormPlayer";
import { FormFieldInput } from "../../create_training_content/FormFieldInput";
import {
  buildFieldsById,
  resolvePiping,
} from "../../create_training_content/formLogic";
import { isDisplayOnlyType } from "../../create_training_content/formFieldHelpers";
import FormInfoScreen from "../../create_training_content/FormInfoScreen";

/**
 * One-question-per-screen form player (survey style). Full-screen overlay
 * modeled on SwipeDeckPlayer: one field per slide, with display-only fields
 * (info screens, headings) rendering as full statement screens. Display
 * conditions and answer piping are applied live; required fields gate Next;
 * Enter advances.
 *
 * An info screen the form ENDS on is the thank-you: it is held back from the
 * flow and shown once the response is submitted. Forms saved before info
 * screens existed carry welcome_screen / thank_you_screen instead, and those
 * still render (the editor folds them into fields on the next save).
 *
 * Modes (see useFormPlayer): "public" (the /f/<token> page, no close X),
 * "tracked" (section-embedded LMS form; writes a LearningFormCompletion),
 * "preview" (editor preview; no writes).
 *
 * Props: mode, form, shareToken?, companyId?, formUuid?, sectionUuid?,
 * employeeID?, initialResponses?, onClose(), onSubmitted(result)?
 */
function FormPlayer({
  mode = "preview",
  form,
  shareToken = null,
  companyId,
  formUuid,
  sectionUuid,
  employeeID,
  initialResponses,
  onClose,
  onSubmitted,
}) {
  const isPublic = mode === "public";
  useNotifyMobileModal(true);

  const player = useFormPlayer({
    mode,
    form,
    shareToken,
    companyId,
    formUuid,
    sectionUuid,
    employeeID,
    initialResponses,
  });
  const {
    fields,
    visible,
    responses,
    setResponse,
    respondentName,
    setRespondentName,
    respondentEmail,
    setRespondentEmail,
    collectRespondent,
    respondentSatisfied,
    isFieldSatisfied,
    status,
    submitError,
    result,
    submit,
    reset,
  } = player;

  const fieldsById = React.useMemo(() => buildFieldsById(fields), [fields]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Slide list. Pre-question slides (welcome, respondent) come first, then one
  // slide per VISIBLE field, then the thank-you (only once submitted).
  const welcome = form?.welcome_screen && (form.welcome_screen.title || form.welcome_screen.description)
    ? form.welcome_screen
    : null;
  const thankYou = form?.thank_you_screen && (form.thank_you_screen.title || form.thank_you_screen.description)
    ? form.thank_you_screen
    : null;

  // The info screen a form ENDS on is its thank-you: held back from the flow and
  // shown once the response is submitted.
  //
  // Read from the SAVED field order, never from `visible`. A mid-form info
  // screen can end up last in `visible` whenever the questions after it are
  // hidden by their display conditions, and that screen is a transition, not an
  // ending — treating it as one would pull it out of the flow and only show it
  // after submit. It still has to be visible itself (its own rule can hide it),
  // and needs a real question ahead of it, or there would be nothing to submit.
  const endingField = React.useMemo(() => {
    const last = fields[fields.length - 1];
    if (!last || last.type !== "info_screen") return null;
    if (!visible.some((f) => f.id === last.id)) return null;
    return visible.some((f) => f.id !== last.id && !isDisplayOnlyType(f.type))
      ? last
      : null;
  }, [fields, visible]);

  const slides = React.useMemo(() => {
    const out = [];
    if (welcome) out.push({ kind: "welcome", key: "welcome" });
    const flow = endingField
      ? visible.filter((f) => f.id !== endingField.id)
      : visible;
    // Leading info screens come BEFORE "who's responding" — the intro is read
    // first, then we ask who they are (the same order the old welcome screen
    // had).
    let i = 0;
    while (i < flow.length && flow[i].type === "info_screen") {
      out.push({ kind: "field", key: flow[i].id, field: flow[i] });
      i += 1;
    }
    if (collectRespondent) out.push({ kind: "respondent", key: "respondent" });
    for (; i < flow.length; i += 1) {
      out.push({ kind: "field", key: flow[i].id, field: flow[i] });
    }
    return out;
  }, [welcome, collectRespondent, visible, endingField]);

  const [rawIndex, setIndex] = React.useState(0);
  const [direction, setDirection] = React.useState(1); // 1 forward, -1 back
  const [showRequiredNudge, setShowRequiredNudge] = React.useState(false);

  // Answering a gate can hide the slide we were on (or later ones), so always
  // read a clamped index rather than trusting state.
  const index = Math.min(rawIndex, Math.max(0, slides.length - 1));
  React.useEffect(() => {
    if (rawIndex !== index) setIndex(index);
  }, [rawIndex, index]);

  const isDone = status === "done";
  const current = slides[index];
  const questionSlides = slides.filter(
    (s) => s.kind === "field" && !isDisplayOnlyType(s.field.type)
  );
  const questionNumber = (slide) => {
    if (!slide || slide.kind !== "field" || isDisplayOnlyType(slide.field.type)) return null;
    return questionSlides.findIndex((s) => s.key === slide.key) + 1;
  };
  const totalQuestions = questionSlides.length;
  // How far through the flow you are, NOT how many answers you've given. An
  // answer-based bar sits still on every info screen (they can't be answered),
  // lags behind whenever an optional question is skipped, and then lurches to
  // 100% on submit. One slide advanced = one step, every time.
  //
  // The CURRENT slide counts, so reaching the last one fills the bar and
  // submitting doesn't move it. Measuring slides left behind instead would park
  // a one-slide form at 0% for its whole life and leave every form short of
  // full until after it was sent.
  const progressPct = isDone
    ? 100
    : slides.length
    ? Math.round(((index + 1) / slides.length) * 100)
    : 0;

  const currentSatisfied = (() => {
    if (!current) return true;
    if (current.kind === "respondent") return respondentSatisfied;
    if (current.kind === "field") return isFieldSatisfied(current.field);
    return true;
  })();

  // The image for the screen on show. It gets a pane of its own rather than
  // sitting inline above the question (see the layout below).
  const slideImage = isDone
    ? endingField?.image_url || null
    : current?.kind === "field"
    ? current.field.image_url || null
    : null;

  const isLast = index >= slides.length - 1;

  const autoAdvanceTimer = React.useRef(null);
  const cancelAutoAdvance = () => clearTimeout(autoAdvanceTimer.current);

  const goNext = React.useCallback(async () => {
    cancelAutoAdvance();
    if (isDone) return;
    if (!currentSatisfied) {
      setShowRequiredNudge(true);
      return;
    }
    setShowRequiredNudge(false);
    if (isLast) {
      const res = await submit();
      if (res && onSubmitted) onSubmitted(res);
      return;
    }
    setDirection(1);
    setIndex((i) => Math.min(i + 1, slides.length - 1));
  }, [isDone, currentSatisfied, isLast, submit, onSubmitted, slides.length]);

  const goBack = React.useCallback(() => {
    cancelAutoAdvance();
    if (isDone || index === 0) return;
    setShowRequiredNudge(false);
    setDirection(-1);
    setIndex((i) => Math.max(0, i - 1));
  }, [isDone, index]);

  // Answer + auto-advance for single-choice controls (Typeform feel). Multi
  // select and text stay on the slide until Next.
  const handleAnswer = (field, value) => {
    setResponse(field.id, value);
    setShowRequiredNudge(false);
    const auto =
      field.type === "single_select" ||
      field.type === "yes_no" ||
      field.type === "scale" ||
      field.type === "nps";
    if (auto && !isLast) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = setTimeout(() => {
        setDirection(1);
        // No upper bound here: the answer may have revealed a follow-up slide;
        // the clamped `index` handles the no-more-slides case.
        setIndex((i) => i + 1);
      }, 260);
    }
  };
  React.useEffect(() => () => clearTimeout(autoAdvanceTimer.current), []);

  // Focus the first text input of a new slide so typing can start immediately
  // (only for text questions; option/scale buttons don't need focus and
  // focusing them would steal Enter).
  const slideRef = React.useRef(null);
  React.useEffect(() => {
    if (isDone) return;
    const t = setTimeout(() => {
      const el = slideRef.current?.querySelector("input[type=text], input[type=email], textarea");
      if (el && current?.kind !== "welcome") el.focus({ preventScroll: true });
    }, 240);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key, isDone]);

  // Scroll affordances. A slide that overflows gave no sign of it: the OK
  // button sat clipped behind the bottom nav on a phone and read as the end of
  // the screen. A fade at whichever edge has content past it says "this
  // continues", and the nav takes a hairline while anything is under it.
  const scrollRef = React.useRef(null);
  const [scrollEdges, setScrollEdges] = React.useState({ top: false, bottom: false });
  const measureScrollEdges = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    // Sub-pixel layout means these never land exactly equal; 1px of slack
    // keeps a fade from flickering at the end of a scroll.
    const overflows = scrollHeight - clientHeight > 1;
    const top = overflows && scrollTop > 1;
    const bottom = overflows && scrollTop + clientHeight < scrollHeight - 1;
    // Bail out when nothing moved. This runs on every scroll event, and a
    // fresh object would re-render the whole player, animations included, for
    // each frame of a scroll on the phones this is meant to help.
    setScrollEdges((previous) =>
      previous.top === top && previous.bottom === bottom
        ? previous
        : { top, bottom }
    );
  }, []);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    // The scroller outlives the slide inside it, so without this a question
    // answered halfway down hands the next one over already scrolled.
    el.scrollTop = 0;
    measureScrollEdges();
    if (typeof ResizeObserver === "undefined") return undefined;
    // Watch the content as well as the viewport: a slide swap, an image
    // finishing loading, or a rewrap all change whether there's more below
    // without any scrolling happening.
    const ro = new ResizeObserver(measureScrollEdges);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [measureScrollEdges, current?.key, isDone]);

  // Keyboard: Enter advances (Shift/Cmd+Enter inside a textarea inserts a
  // newline as usual, plain Enter in a textarea also just newlines), Escape
  // closes (never on the public page, there's nothing to close to).
  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && !isPublic) {
        e.preventDefault();
        onClose && onClose();
        return;
      }
      if (e.key === "Enter") {
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "textarea" && !(e.metaKey || e.ctrlKey)) return;
        if (tag === "button" || tag === "select") return;
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, onClose, isPublic]);

  // Body scroll lock while open.
  React.useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const pipe = (text) => resolvePiping(text, responses, fieldsById);

  const handleRestart = () => {
    reset();
    setIndex(0);
    setDirection(1);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render

  const variants = {
    enter: (dir) => ({ y: dir > 0 ? 40 : -40, opacity: 0 }),
    center: { y: 0, opacity: 1 },
    exit: (dir) => ({ y: dir > 0 ? -40 : 40, opacity: 0 }),
  };

  const renderSlideBody = () => {
    if (isDone) {
      // Public results carry scoring_enabled + score; a tracked completion
      // carries score/is_passed/grading_status. Show whatever is known.
      const scored = result?.score !== null && result?.score !== undefined;
      const pendingGrade = result?.grading_status === "pending";
      return (
        <div className={endingField ? "" : "text-center"}>
          {endingField ? (
            <FormInfoScreen
              label={pipe(endingField.label)}
              help={pipe(endingField.help)}
              variant="slide"
            />
          ) : (
            <>
              <CheckCircleIcon className="text-emerald-500" style={{ fontSize: 56 }} />
              <h1 className="mt-4 text-2xl font-semibold text-slate-900 sm:text-xl">
                {thankYou?.title || "Thanks! Your response was submitted."}
              </h1>
              {thankYou?.description && (
                <p className="mt-2 text-base text-slate-600 whitespace-pre-wrap">
                  {thankYou.description}
                </p>
              )}
            </>
          )}
          {pendingGrade && (
            <p className="mt-4 text-sm text-amber-700">
              Submitted, awaiting manager review.
            </p>
          )}
          {scored && !pendingGrade && (
            <p className="mt-4 text-sm text-slate-600">
              Your score: <span className="font-semibold">{result.score}</span>
              {result.is_passed === true
                ? " · Passed"
                : result.is_passed === false
                ? " · Not passed"
                : ""}
            </p>
          )}
          {mode === "preview" && (
            <p className="mt-4 text-xs text-slate-400">
              Preview only, nothing was saved.
            </p>
          )}
          <div
            className={[
              "mt-8 flex items-center gap-3",
              endingField ? "" : "justify-center",
            ].join(" ")}
          >
            {isPublic ? (
              <button
                type="button"
                onClick={handleRestart}
                className="text-sm font-medium text-violet-700 hover:text-violet-800"
              >
                Submit another response
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 text-base font-semibold"
              >
                Done
              </button>
            )}
          </div>
        </div>
      );
    }

    if (!current) {
      return (
        <div className="text-center text-slate-500 text-sm">This form has no questions yet.</div>
      );
    }

    if (current.kind === "welcome") {
      return (
        <div>
          <h1 className="text-3xl font-bold text-slate-900 sm:text-2xl">
            {welcome.title || form?.name}
          </h1>
          {welcome.description && (
            <p className="mt-3 text-base text-slate-600 whitespace-pre-wrap">
              {welcome.description}
            </p>
          )}
          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={goNext}
              className="rounded-xl bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 text-base font-semibold"
            >
              Start
            </button>
            {totalQuestions > 0 && (
              <span className="text-xs text-slate-500">
                {totalQuestions} {totalQuestions === 1 ? "question" : "questions"}
              </span>
            )}
          </div>
        </div>
      );
    }

    if (current.kind === "respondent") {
      return (
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-xl">
            First, who's responding?
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Your name and email are shared with the team collecting this form.
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Your name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoFocus
                value={respondentName}
                onChange={(e) => setRespondentName(e.target.value)}
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Your email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={respondentEmail}
                onChange={(e) => setRespondentEmail(e.target.value)}
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
              />
            </div>
          </div>
          {showRequiredNudge && !currentSatisfied && (
            <p className="mt-3 text-sm text-red-600">Please enter your name and email to continue.</p>
          )}
        </div>
      );
    }

    // Field slide
    const f = current.field;
    if (f.type === "info_screen") {
      return (
        <FormInfoScreen label={pipe(f.label)} help={pipe(f.help)} variant="slide" />
      );
    }
    if (f.type === "heading") {
      return (
        <div>
          <h2 className="text-2xl font-bold text-slate-900 sm:text-xl">{pipe(f.label)}</h2>
          {f.help && (
            <p className="mt-3 text-base text-slate-600 whitespace-pre-wrap">{pipe(f.help)}</p>
          )}
        </div>
      );
    }
    const n = questionNumber(current);
    return (
      <div>
        {/* The counter sits ABOVE the question, not beside it: inline, it
            indented the first line while every wrapped line (and the options,
            and the button) stayed flush left. */}
        {n && (
          <div className="mb-1.5 text-sm font-semibold text-violet-700 tabular-nums">
            {n}
            <span className="text-slate-400 font-normal"> / {totalQuestions}</span>
          </div>
        )}
        <h2 className="text-2xl font-semibold text-slate-900 sm:text-xl">
          {pipe(f.label)}
          {f.required && <span className="text-red-500"> *</span>}
        </h2>
        {f.help && f.type !== "checkbox" && (
          <p className="mt-2 text-sm text-slate-500 whitespace-pre-wrap">{pipe(f.help)}</p>
        )}
        <div className="mt-6">
          <FormFieldInput
            // Checkbox renders its own help as the tick label, so pipe it too.
            field={f.help ? { ...f, help: pipe(f.help) } : f}
            value={responses[f.id]}
            onChange={(val) => handleAnswer(f, val)}
          />
        </div>
        {showRequiredNudge && !currentSatisfied && (
          <p className="mt-3 text-sm text-red-600">Please answer this question to continue.</p>
        )}
      </div>
    );
  };

  const showNext = !isDone && current && current.kind !== "welcome";
  // The first slide of a form that opens on an info screen is its welcome, so
  // its button reads "Start" (and shows how long the form is) exactly like the
  // legacy welcome screen did.
  const isOpeningScreen =
    index === 0 && current?.kind === "field" && current.field.type === "info_screen";
  const nextLabel = isLast
    ? status === "submitting"
      ? "Submitting…"
      : "Submit"
    : isOpeningScreen
    ? "Start"
    : "OK";

  return (
    <div className="fixed inset-0 z-[1400] bg-slate-50 flex flex-col">
      {/* Progress */}
      <div className="h-1.5 w-full bg-slate-200 shrink-0">
        <div
          className="h-full bg-violet-700 transition-[width] duration-300"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Body: the content column beside a full-height image pane. The pane
          starts under the progress bar and runs to the bottom edge, so it spans
          the window the way the image does on a Typeform screen — the top
          chrome and the bottom nav sit INSIDE the content column, not above and
          below the image.

          Breakpoints here are max-width (see tailwind.config.js: `lg` is
          <=1023px), so the split applies from 1024px up and the halves stack
          below that. A half column narrower than ~500px can't hold a question
          and its options comfortably, which is why this stacks at tablet width
          rather than waiting for phones. */}
      <div className="flex-1 min-h-0 flex lg:flex-col">
        {/* min-h-0 as well as min-w-0: stacked (lg and down) this is a column
            flex item, whose default min-height:auto refuses to shrink below its
            content. The column then grew past the viewport, taking the scroll
            container inside it along, so a long list of options had no way to
            scroll and its Next button sat off screen under a locked body. */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {/* Top chrome */}
        <div className="flex items-center justify-between px-5 py-3 shrink-0">
          <div className="text-sm font-medium text-slate-500 truncate">{form?.name}</div>
          {!isPublic && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close form"
              className="w-9 h-9 rounded-full hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Slide */}
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollRef}
            onScroll={measureScrollEdges}
            className="h-full overflow-y-auto"
          >
            <div
            className={[
              "min-h-full flex justify-center px-6 py-10 sm:px-5 sm:py-6",
              // Centred normally; under a stacked banner the leftover space is
              // tall and mostly empty, so anchor the question to the top of it.
              slideImage ? "items-center lg:items-start" : "items-center",
            ].join(" ")}
          >
              <div className="w-full max-w-xl">
                <AnimatePresence mode="wait" custom={direction} initial={false}>
                  <motion.div
                    ref={slideRef}
                    key={isDone ? "done" : current?.key || "empty"}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.22, ease: "easeOut" }}
                  >
                    {renderSlideBody()}
                    {submitError && !isDone && (
                      <p className="mt-3 text-sm text-red-600">{submitError}</p>
                    )}
                    {showNext && (
                      <div className="mt-8 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={goNext}
                          disabled={status === "submitting"}
                          className={[
                            "rounded-xl px-6 py-3 text-base font-semibold text-white transition-colors disabled:opacity-60",
                            currentSatisfied
                              ? "bg-violet-700 hover:bg-violet-800"
                              : "bg-slate-400 hover:bg-slate-500",
                          ].join(" ")}
                        >
                          {nextLabel}
                        </button>
                        {isOpeningScreen && totalQuestions > 0 ? (
                          <span className="text-xs text-slate-500">
                            {totalQuestions} {totalQuestions === 1 ? "question" : "questions"}
                            {isPublic && !collectRespondent ? " · no name collected" : ""}
                          </span>
                        ) : !isLast ? (
                          <span className="text-xs text-slate-400 sm:hidden">
                            press <span className="font-semibold">Enter ↵</span>
                          </span>
                        ) : null}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Edge fades sit over the scroller, never under the pointer, and
              only when there is something past that edge to reach. */}
          {scrollEdges.top && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-slate-50 to-transparent" />
          )}
          {scrollEdges.bottom && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-slate-50 to-transparent" />
          )}
        </div>

        {/* Bottom nav */}
        {!isDone && slides.length > 1 && (
          <div
            className={`shrink-0 flex items-center justify-end gap-1 px-5 py-3 transition-colors ${
              scrollEdges.bottom ? "border-t border-slate-200" : "border-t border-transparent"
            }`}
          >
            <button
              type="button"
              onClick={goBack}
              disabled={index === 0}
              aria-label="Previous"
              className="w-9 h-9 rounded-lg bg-violet-700 text-white flex items-center justify-center disabled:opacity-30 hover:bg-violet-800"
            >
              <ArrowUpwardIcon style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={isLast || !currentSatisfied}
              aria-label="Next"
              className="w-9 h-9 rounded-lg bg-violet-700 text-white flex items-center justify-center disabled:opacity-30 hover:bg-violet-800"
            >
              <ArrowDownwardIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}
        </div>

        {/* The pane opens and closes over the SAME 220ms as the slide change.
            Mounting it outright made the column snap to full width the instant
            you hit OK — before the outgoing question had faded — which read as a
            glitch rather than a transition.

            The image itself is held at its final width (50vw) and clipped by the
            pane, so it wipes in and out instead of squashing. lg:!w-full beats
            the inline width framer-motion writes, keeping the stacked banner
            full-width below 1024px. */}
        <AnimatePresence initial={false}>
          {slideImage && (
            <motion.div
              key="image-pane"
              initial={{ width: 0 }}
              animate={{ width: "50%" }}
              exit={{ width: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              // Stacked, the banner keeps the photo's aspect ratio up to a
              // share of the viewport, the way Typeform does — a fixed height
              // cropped a 3:2 photo down to a letterbox strip and threw away
              // most of the picture. Capped so it can't push the question off
              // screen (tighter on phones, where the cap is what actually
              // applies).
              className="shrink-0 overflow-hidden bg-slate-100 lg:!w-full lg:h-auto lg:max-h-[60vh] sm:max-h-[45vh] lg:order-first"
            >
              {/* Crossfade between two screens that both have an image (the pane
                  stays put, only the picture changes). object-cover is right
                  HERE, unlike the scrolling surfaces: this is a fixed frame the
                  image is meant to fill. */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.img
                  key={slideImage}
                  src={slideImage}
                  alt=""
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="w-[50vw] h-full object-cover lg:!w-full lg:h-auto lg:max-h-[60vh] sm:max-h-[45vh]"
                />
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default FormPlayer;
